// Fixed top-left logo + section breadcrumb.
// - Click the logo -> return to landing page.
// - Click the category name -> scroll to the top of the current section page.
// - The section name updates as the user scrolls between snap-locked sections,
//   driven by IntersectionObserver against elements with data-breadcrumb.
// - On the landing page only the logo is rendered (no breadcrumb pill).

import { sectionFromPath } from "./url";

const CATEGORY_LABEL = {
  pension: "Pension Ponzi",
  housing: "Housing Ponzi",
  education: "Education Ponzi",
  wages: "Wages Ponzi",
} as const;

const LOGO_MARKUP =
  '<a class="site-mark" href="/" aria-label="Boomer Ponzi home">' +
  '<img src="/boomer-ponzi-logo.png" alt="Boomer Ponzi" />' +
  "</a>";

export function mountBreadcrumb(): void {
  const section = sectionFromPath();

  const root = document.createElement("nav");
  root.className = "site-nav";

  if (section === "landing") {
    root.innerHTML = LOGO_MARKUP;
    document.body.appendChild(root);
    return;
  }

  const categoryLabel = CATEGORY_LABEL[section];

  root.innerHTML = `
    ${LOGO_MARKUP}
    <div class="site-crumb">
      <button type="button" class="crumb-category" aria-label="Back to top">${categoryLabel}</button>
      <span class="crumb-sep" data-role="sep" hidden>/</span>
      <span class="crumb-section" data-role="section"></span>
    </div>
  `;
  document.body.appendChild(root);

  const sectionLabelEl = root.querySelector<HTMLSpanElement>('[data-role="section"]')!;
  const sepEl = root.querySelector<HTMLSpanElement>('[data-role="sep"]')!;
  const categoryBtn = root.querySelector<HTMLButtonElement>(".crumb-category")!;
  categoryBtn.addEventListener("click", () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  });

  function tag(el: HTMLElement): string | null {
    // 1. Explicit data attribute wins.
    const explicit = el.getAttribute("data-breadcrumb");
    if (explicit) return explicit;
    // 2. Section h2 text (most panels).
    const h2 = el.querySelector("h2");
    const h2Text = h2?.textContent?.trim().replace(/[.]$/, "");
    if (h2Text) return h2Text;
    // 3. Section eyebrow text (the hero, boomer-section, etc).
    const eyebrow = el.querySelector(".eyebrow");
    const eyText = eyebrow?.textContent?.trim();
    if (eyText) {
      // Capitalize each word in eyebrow text for breadcrumb display.
      return eyText
        .toLowerCase()
        .split(" ")
        .map((w) => (w.length > 2 ? w[0].toUpperCase() + w.slice(1) : w))
        .join(" ");
    }
    return null;
  }

  // Track the section closest to the top of the viewport.
  const observed = Array.from(
    document.querySelectorAll<HTMLElement>("main > section, main > header"),
  );
  const labelByEl = new Map<HTMLElement, string>();
  for (const el of observed) {
    const t = tag(el);
    if (t) labelByEl.set(el, t);
  }

  let current: HTMLElement | null = null;
  function setCurrent(el: HTMLElement) {
    if (current === el) return;
    current = el;
    const label = labelByEl.get(el);
    if (label) {
      sectionLabelEl.textContent = label;
      sepEl.hidden = false;
    } else {
      sectionLabelEl.textContent = "";
      sepEl.hidden = true;
    }
  }

  // Use rootMargin to bias toward the section whose top is near the viewport top.
  // mandatory snap means at any time one section is "the" section, but a viewport
  // can show two during a snap transition. Pick whichever is most visible above
  // the centre line.
  const io = new IntersectionObserver(
    (entries) => {
      let best: { el: HTMLElement; ratio: number } | null = null;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target as HTMLElement;
        if (!labelByEl.has(el)) continue;
        if (!best || entry.intersectionRatio > best.ratio) {
          best = { el, ratio: entry.intersectionRatio };
        }
      }
      if (best) setCurrent(best.el);
    },
    {
      threshold: [0, 0.25, 0.5, 0.75, 1],
      rootMargin: "-10% 0px -45% 0px",
    },
  );
  for (const el of observed) io.observe(el);

  // Initial label: first observed element.
  if (observed.length) setCurrent(observed[0]);
}
